import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { whatsappUrl } from "@/lib/whatsapp";
import { WhatsappGlyph } from "@/components/WhatsappFab";
import { BOUTIQUE_ENABLED } from "@/lib/features";

const allLinks = [
  { to: "/#methode", label: "La méthode" },
  { to: "/boutique", label: "Boutique", boutique: true },
  { to: "/a-propos", label: "À propos" },
  { to: "/blog", label: "Études & blog" },
  { to: "/#faq", label: "FAQ" },
];
const links = allLinks.filter((l) => !l.boutique || BOUTIQUE_ENABLED);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // On the home page, the hero is dark — keep light text until scrolled.
  // On all other pages, the page starts on a pearl background — use dark text immediately.
  const onDarkHero = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const textColor = onDarkHero ? "text-pearl" : "text-ink";
  const subColor = onDarkHero ? "text-pearl/55" : "text-mute";
  const bgClass = scrolled
    ? "bg-pearl/90 backdrop-blur-xl border-b border-ink/10"
    : "bg-transparent";
  const navLinkClass = onDarkHero
    ? "nav-link-dark"
    : "nav-link";
  const ctaClass = onDarkHero ? "btn-white" : "btn-primary";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
      style={{ top: "var(--promo-bar-h, 0px)" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src={onDarkHero ? "/brand/shinrai-logo-slogan-white.png" : "/brand/shinrai-logo-slogan-transparent.png"}
            alt="ShinRai Consulting — switch on confidence"
            className="h-12 sm:h-14 w-auto transition-opacity duration-300"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) =>
            l.to.startsWith("/#") ? (
              <a
                key={l.to}
                href={l.to}
                className={`${navLinkClass} ${textColor}`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                className={`${navLinkClass} ${textColor}`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            )
          )}
          <a
            href={whatsappUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp"
            aria-label="WhatsApp"
            className={`hidden md:inline-flex items-center justify-center w-10 h-10 border transition-colors ${
              onDarkHero
                ? "border-pearl/30 text-pearl hover:bg-[#25D366] hover:border-[#25D366]"
                : "border-ink/15 text-ink hover:bg-[#25D366] hover:text-white hover:border-[#25D366]"
            }`}
          >
            <WhatsappGlyph size={18} />
          </a>
          <Link
            to="/#diagnostic"
            className={ctaClass}
            data-testid="nav-cta-diagnostic"
          >
            Diagnostic gratuit
            <ArrowUpRight size={16} />
          </Link>
        </nav>

        <button
          className={`lg:hidden p-2 ${textColor}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-pearl border-t border-ink/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) =>
              l.to.startsWith("/#") ? (
                <a
                  key={l.to}
                  href={l.to}
                  className="text-lg font-serif text-ink"
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="text-lg font-serif text-ink"
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              )
            )}
            <Link to="/#diagnostic" className="btn-primary justify-center" data-testid="mobile-cta-diagnostic">
              Diagnostic gratuit
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
